import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { Container, Button, Row, Col, Alert } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Formik, FormikHelpers } from 'formik';
import { normalizeDescribeMeal } from '../helpers/normalizers';
import {
  useToast,
  useTranslator,
  useAppContext,
  FormikInput as Input,
} from '@front-activerse/common-components';
import validationSchema from './validationSchema';
import { withForcedAiError } from '../helpers/devForcedError';

interface ScanPlateDetailsContainerProps {
  scanPlate: (image: File, imageContext: string) => Promise<unknown>;
  cancelScanPlate: () => void;
  fromUserMeals?: boolean;
}

interface LocationState {
  imageFile?: File;
  imageUrl?: string;
}

const ScanPlateDetailsContainer: React.FC<ScanPlateDetailsContainerProps> = ({
  scanPlate,
  cancelScanPlate,
  fromUserMeals,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { toastError } = useToast();
  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const { t } = useTranslator();

  const isCanceledRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formikValues = {
    description: '',
  };

  const {
    images: { aiStarDarkIcon, aiStarLightIcon, patternDot },
  } = useAppContext();

  useEffect(() => {
    setNoFoodError(false);
    setRateLimitExceeded(false);
    // Load image from location state
    if (state?.imageFile) {
      setImageFile(state.imageFile);
      const url = URL.createObjectURL(state.imageFile);
      setImageUrl(url);

      // Cleanup URL on unmount
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (state?.imageUrl) {
      setImageUrl(state.imageUrl);
    } else {
      // No image provided, redirect back
      // navigate(-1);
    }
  }, [state]);

  const handleSubmit = async (
    values: typeof formikValues,
    { setSubmitting }: FormikHelpers<typeof formikValues>
  ) => {
    setNoFoodError(false);
    setRateLimitExceeded(false);

    if (!imageFile) {
      toastError('No image to analyze');
      return;
    }

    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const response: any = withForcedAiError(await scanPlate(imageFile, values.description));

      if (response.__typename === 'ScanPlateSuccess') {
        sessionStorage.setItem('tracker_depth', '3');

        // calculate random id
        const dietMealId = Math.floor(Math.random() * 1000000000);

        const normalizedProps = normalizeDescribeMeal({
          ...response,
          dietMealId,
        });

        if (fromUserMeals) {
          navigate(`/meals/user-meals/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        } else {
          navigate(`/tracker/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        }
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else {
        toastError();
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }

      // For other errors, reset submitting state and show error
      setSubmitting(false);
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.analizeMeal.ingredients.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={handleSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        key={imageUrl}
      >
        {({ isValid, values, isSubmitting, setSubmitting }) => {
          const handleCancel = () => {
            // Set flag to prevent Formik from retrying the submit
            isCanceledRef.current = true;
            // Cancel the ongoing GraphQL mutation
            cancelScanPlate();
            // Reset submitting state immediately
            setSubmitting(false);
            // Reset flag to allow retry
            setTimeout(() => {
              isCanceledRef.current = false;
            }, 0);
          };

          return (
            <Form>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col md={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <Row>
                            <Col xs="12" md="4">
                              {/* Image preview */}
                              {imageUrl && (
                                <div className="scan-plate-image-preview">
                                  <img src={imageUrl} alt="Plate to scan" className="img-fluid" />

                                  <div
                                    className="animation-loader-wrapper"
                                    style={{ opacity: isSubmitting ? 1 : 0 }}
                                  >
                                    <img src={patternDot} alt="" />
                                  </div>
                                </div>
                              )}
                            </Col>
                            <Col xs="12" md="8">
                              <Input
                                name="description"
                                type="textarea"
                                placeholder={t('tracker.scanFood.noteInput.placeholder')}
                                required
                                translateErrMsg
                                innerRef={textareaRef}
                                onFocus={() => {
                                  setTimeout(() => {
                                    textareaRef.current?.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                    });
                                  }, 500);
                                }}
                              />

                              {noFoodError && (
                                <Alert color="warning" className="mt-xl mb-0" fade={false}>
                                  <p className="mb-0">
                                    {t('tracker.describeFood.analyze.noFood.notification')}
                                  </p>
                                </Alert>
                              )}

                              {rateLimitExceeded && (
                                <Alert color="danger" className="mt-xl mb-0" fade={false}>
                                  <p className="mb-0">
                                    {t('tracker.limitUsageError.notification')}
                                  </p>
                                </Alert>
                              )}
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    {!isSubmitting ? (
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          <Button
                            type="button"
                            color="link"
                            className="w-100 mb-s"
                            onClick={() => {
                              navigate(-1);
                            }}
                          >
                            <span className="medium">
                              {t('tracker.scanFood.button.retakePhoto')}
                            </span>
                          </Button>

                          <Button
                            type="submit"
                            color="primary"
                            className="w-100"
                            disabled={!isValid || !imageFile}
                          >
                            <img
                              src={aiStarLightIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.scanFood.button.analyze')}
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12" className="text-center">
                          <p className="description text-text-variant-color2">
                            {t('tracker.describeFood.loader.analyzing')}
                          </p>
                        </Col>
                        <Col xs="4">
                          <Button type="button" color="link" onClick={handleCancel}>
                            {t('tracker.describeFood.loader.cancelButton')}
                          </Button>
                        </Col>
                        <Col xs="4">
                          <div className="ai-star">
                            <img src={aiStarDarkIcon} alt="" />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default ScanPlateDetailsContainer;
